import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { useActiveProgramId } from "./programs/program-scope";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { OutletsApi, outletKeys } from "@/api/perkpoint/outlets";
import {
  MembersApi,
  initialsOf,
  memberDisplayName,
  memberKeys,
  relativeDays,
  titleCase,
  type ApiMemberRow,
  type MemberListQuery,
} from "@/api/perkpoint/members";
import { AdjustMemberPointsDialog } from "./AdjustMemberPointsDialog";
import { DateRange } from "react-day-picker";
import {
  Ban,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Coins,
  Columns3,
  Crown,
  Eye,
  Gift,
  LockOpen,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  SignalHigh,
  SignalLow,
  SignalMedium,
  SlidersHorizontal,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type Tier = string;
type Activity = "High" | "Medium" | "Low";
type MemberStatus = "Active" | "At risk" | "Blocked" | "Closed";
type QuickTab =
  | "all"
  | "active"
  | "at-risk"
  | "new"
  | "gold-above";

type MemberRow = {
  id: string;
  memberId: string;
  name: string;
  email: string;
  initials: string;
  avatarClass: string;
  tier: Tier;
  pointsBalance: number;
  pointsEarned: number;
  activity: Activity;
  status: MemberStatus;
  lastActive: string;
  nextAction: string;
};

const QUICK_TABS: { id: QuickTab; label: string }[] = [
  { id: "all", label: "All members" },
  { id: "active", label: "Active" },
  { id: "at-risk", label: "At risk" },
  { id: "new", label: "New this month" },
  { id: "gold-above", label: "Gold & above" },
];

const QUICK_TAB_PARAM: Record<QuickTab, MemberListQuery["quick_tab"]> = {
  all: "all",
  active: "active",
  "at-risk": "at_risk",
  new: "new",
  "gold-above": "gold_above",
};

const STATUS_LABEL: Record<string, MemberStatus> = {
  active: "Active",
  at_risk: "At risk",
  blocked: "Blocked",
  closed: "Closed",
};

const AVATAR_CLASSES = [
  "bg-emerald-100 text-emerald-800",
  "bg-sky-100 text-sky-800",
  "bg-amber-100 text-amber-800",
  "bg-violet-100 text-violet-800",
  "bg-rose-100 text-rose-800",
  "bg-teal-100 text-teal-800",
];

function rowFromApi(row: ApiMemberRow): MemberRow {
  const hash = [...row.member_id].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  const displayName = memberDisplayName(row) || "Unnamed member";
  return {
    id: row.member_id,
    memberId: row.external_member_id,
    name: displayName,
    email: row.email || row.phone || "",
    initials: initialsOf(displayName),
    avatarClass: AVATAR_CLASSES[hash % AVATAR_CLASSES.length],
    tier: row.tier?.name ?? "—",
    pointsBalance: row.points.available,
    pointsEarned: row.points.lifetime_earned,
    activity: titleCase(row.activity) as Activity,
    status: STATUS_LABEL[row.display_status] ?? "Active",
    lastActive: relativeDays(row.last_activity_at),
    nextAction: row.next_action,
  };
}

function isoDay(date: Date | undefined) {
  return date ? format(date, "yyyy-MM-dd") : undefined;
}

/** "18 Jun 1994" or "1994-06-18" → "1994-06-18"; undefined when blank or unreadable. */
function toIsoDate(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? undefined : format(parsed, "yyyy-MM-dd");
}

function formatPts(value: number) {
  return `${value.toLocaleString("en-IN")} pts`;
}

function TierBadge({ tier }: { tier: Tier }) {
  if (tier === "Gold") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700">
        <Crown className="h-3.5 w-3.5 text-amber-500" />
        Gold
      </span>
    );
  }
  if (tier === "Silver") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600">
        <Star className="h-3.5 w-3.5 fill-slate-300 text-slate-400" />
        Silver
      </span>
    );
  }
  if (tier === "Bronze") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-700">
        <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-orange-400 text-[8px] font-bold text-orange-600">
          B
        </span>
        Bronze
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">
      {tier === "Platinum" || tier === "Prestige" ? (
        <Crown className="h-3.5 w-3.5 text-violet-500" />
      ) : null}
      {tier}
    </span>
  );
}

function ActivityCell({ activity }: { activity: Activity }) {
  const Icon =
    activity === "High"
      ? SignalHigh
      : activity === "Medium"
        ? SignalMedium
        : SignalLow;
  const color =
    activity === "High"
      ? "text-green-600"
      : activity === "Medium"
        ? "text-lime-600"
        : "text-amber-500";

  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm", color)}>
      <Icon className="h-4 w-4" />
      {activity}
    </span>
  );
}

function StatusCell({ status }: { status: MemberStatus }) {
  const dotClass =
    status === "Active"
      ? "bg-green-500"
      : status === "Blocked"
        ? "bg-red-500"
        : status === "Closed"
          ? "bg-slate-400"
          : "bg-orange-400";

  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-700">
      <span className={cn("h-2 w-2 rounded-full", dotClass)} />
      {status}
    </span>
  );
}

function FieldLabel({
  children,
  required,
  htmlFor,
}: {
  children: ReactNode;
  required?: boolean;
  htmlFor?: string;
}) {
  return (
    <Label htmlFor={htmlFor} className="text-sm font-medium text-slate-700">
      {children}
      {required ? <span className="ml-0.5 text-red-500">*</span> : null}
    </Label>
  );
}

function DateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="relative">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="DD MMM YYYY"
          className="h-10 pr-10"
        />
        <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
    </div>
  );
}

function AddMemberSheet({
  open,
  onOpenChange,
  programId,
  startingTier,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  programId: string | null;
  startingTier: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mobile, setMobile] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [anniversary, setAnniversary] = useState("");
  const [gender, setGender] = useState<string>("");
  const [city, setCity] = useState("");
  const [outlet, setOutlet] = useState("");
  const outletsQuery = useQuery({
    queryKey: outletKeys.list(programId ?? "", { page_size: 100 }),
    queryFn: () => OutletsApi.list(programId!, { page: 1, page_size: 100 }),
    enabled: open && Boolean(programId),
  });
  const [referredBy, setReferredBy] = useState("");
  const [whatsappConsent, setWhatsappConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);

  const closeSheet = () => onOpenChange(false);

  const reset = () => {
    setMobile("");
    setFullName("");
    setEmail("");
    setDob("");
    setAnniversary("");
    setGender("");
    setCity("");
    setOutlet("");
    setReferredBy("");
    setWhatsappConsent(false);
    setSmsConsent(false);
    setEmailConsent(false);
  };

  const enrol = useMutation({
    mutationFn: () =>
      MembersApi.enrol(programId!, {
        full_name: fullName.trim(),
        phone: mobile.trim(),
        email: email.trim() || undefined,
        gender: gender || undefined,
        date_of_birth: toIsoDate(dob),
        anniversary: toIsoDate(anniversary),
        city: city.trim() || undefined,
        enrolled_outlet: outlet || undefined,
        referred_by: referredBy.trim() || undefined,
        consents: { whatsapp: whatsappConsent, sms: smsConsent, email: emailConsent },
      }),
  });

  const submit = (openAfter: boolean) => {
    if (!programId) {
      toast.error("No program selected.");
      return;
    }
    if (!fullName.trim() || !mobile.trim()) {
      toast.error("Enter the member's full name and mobile number.");
      return;
    }
    if (emailConsent && !email.trim()) {
      toast.error("Add an email before enabling email marketing.");
      return;
    }
    for (const [label, value] of [["Date of birth", dob], ["Anniversary", anniversary]] as const) {
      if (value && !toIsoDate(value)) {
        toast.error(`${label}: use DD MMM YYYY, e.g. 18 Jun 1994.`);
        return;
      }
    }
    enrol.mutate(undefined, {
      onSuccess: (member) => {
        queryClient.invalidateQueries({ queryKey: memberKeys.all });
        toast.success(
          `${member.name ?? "Member"} enrolled as ${member.external_member_id}${
            member.welcome_bonus_points ? ` · ${member.welcome_bonus_points} welcome points` : ""
          }`,
        );
        reset();
        closeSheet();
        if (openAfter) navigate(`/perkpoint/member/${member.member_id}`);
      },
      onError: (error) => toast.error(apiErrorMessage(error, "Could not enrol the member")),
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
      >
        <div className="flex-1 overflow-y-auto px-5 pb-4 pt-6 sm:px-6">
          <div className="pr-8">
            <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">
              Add member
            </SheetTitle>
            <SheetDescription className="mt-1 text-sm text-muted-foreground">
              Enrol a member and record their consent.
            </SheetDescription>
          </div>

          <div className="mt-6 space-y-6">
            <section className="space-y-4">
              <h3 className="text-base font-semibold text-slate-900">Details</h3>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="add-mobile" required>
                    Mobile number
                  </FieldLabel>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="inline-flex h-10 shrink-0 items-center gap-1 rounded-md border border-input bg-white px-2.5 text-sm text-slate-700"
                    >
                      +91
                      <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                    <Input
                      id="add-mobile"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="Enter mobile number"
                      className="h-10"
                    />
                  </div>
                  <p className="text-xs text-sky-700">
                    We&apos;ll check if this number is already registered.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="add-name" required>
                    Full name
                  </FieldLabel>
                  <Input
                    id="add-name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter full name"
                    className="h-10"
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="add-email">Email (optional)</FieldLabel>
                  <Input
                    id="add-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email"
                    className="h-10"
                  />
                </div>

                <DateField
                  id="add-dob"
                  label="Date of birth (optional)"
                  value={dob}
                  onChange={setDob}
                />

                <DateField
                  id="add-anniversary"
                  label="Anniversary (optional)"
                  value={anniversary}
                  onChange={setAnniversary}
                />

                <div className="space-y-1.5">
                  <FieldLabel>Gender (optional)</FieldLabel>
                  <Select value={gender || undefined} onValueChange={setGender}>
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer-not">
                        Prefer not to say
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="add-city">
                    City or area (optional)
                  </FieldLabel>
                  <Input
                    id="add-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter city or area"
                    className="h-10"
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required>Enrolled outlet</FieldLabel>
                  <Select
                    value={outlet || undefined}
                    onValueChange={setOutlet}
                  >
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue placeholder="Select outlet" />
                    </SelectTrigger>
                    <SelectContent>
                      {(outletsQuery.data?.items ?? []).map((item) => (
                        <SelectItem key={item.id} value={item.name}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-sky-700">
                    {outletsQuery.data && outletsQuery.data.total === 0
                      ? "Add an outlet before enrolling members at a location."
                      : "The location this member joined from."}
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <FieldLabel htmlFor="add-referred">
                  Referred by (optional)
                </FieldLabel>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="add-referred"
                    value={referredBy}
                    onChange={(e) => setReferredBy(e.target.value)}
                    placeholder="Search member by name or ID"
                    className="h-10 pl-9"
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-sm font-medium text-slate-700">Member ID</p>
                  <Input
                    placeholder="Auto-generated"
                    disabled
                    className="mt-2 h-10 bg-white"
                  />
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Will be assigned after saving.
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-sm font-medium text-slate-700">
                    Starting tier
                  </p>
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-sm font-medium text-amber-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-700" />
                      {startingTier || "Program default"}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Assigned automatically.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 rounded-xl border border-green-200 bg-green-50 p-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700">
                  <Gift className="h-4 w-4" />
                </div>
                <div className="min-w-0 text-sm">
                  <p className="text-slate-800">
                    <span className="font-semibold">Welcome bonus: 300 pts</span>{" "}
                    will be issued (rule RL-003)
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    A welcome message will be sent to the member if marketing
                    consent is enabled.
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-slate-200 pt-5">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Consent (optional)
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Get the member&apos;s consent to receive communications.
                </p>
              </div>

              <div className="space-y-3">
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
                  <Checkbox
                    checked={whatsappConsent}
                    onCheckedChange={(checked) =>
                      setWhatsappConsent(Boolean(checked))
                    }
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-900">
                      WhatsApp marketing
                    </span>
                    <span className="mt-0.5 block text-sm text-muted-foreground">
                      Send offers, new rewards and updates on WhatsApp.
                    </span>
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
                  <Checkbox
                    checked={smsConsent}
                    onCheckedChange={(checked) =>
                      setSmsConsent(Boolean(checked))
                    }
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-900">
                      SMS marketing
                    </span>
                    <span className="mt-0.5 block text-sm text-muted-foreground">
                      Send offers, new rewards and updates via SMS.
                    </span>
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
                  <Checkbox
                    checked={emailConsent}
                    onCheckedChange={(checked) =>
                      setEmailConsent(Boolean(checked))
                    }
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-900">
                      Email marketing
                    </span>
                    <span className="mt-0.5 block text-sm text-muted-foreground">
                      Send offers, new rewards and updates via email.
                    </span>
                  </span>
                </label>
              </div>

              <p className="text-xs text-muted-foreground">
                Consent is recorded with date and time.
              </p>
            </section>
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-wrap gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:px-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={closeSheet}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1 border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800"
            onClick={() => submit(false)}
            disabled={enrol.isPending}
          >
            Add member
          </Button>
          <Button
            type="button"
            className="flex-1 rounded-full bg-[#1B7A45] text-white hover:bg-[#16653A]"
            onClick={() => submit(true)}
            disabled={enrol.isPending}
          >
            Add and open member
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function AllMembers() {
  const queryClient = useQueryClient();
  const { programId } = useActiveProgramId();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [quickTab, setQuickTab] = useState<QuickTab>("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [segmentFilter, setSegmentFilter] = useState("all");
  const [activityFilter, setActivityFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [memberToBlock, setMemberToBlock] = useState<MemberRow | null>(null);
  const [memberToAdjust, setMemberToAdjust] = useState<MemberRow | null>(null);
  // Filters enrolment date; empty means all members.
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const pageSize = 10;

  const programQuery = useQuery({
    queryKey: programKeys.detail(programId ?? ""),
    queryFn: () => ProgramsApi.get(programId!),
    enabled: Boolean(programId),
  });
  const segmentsQuery = useQuery({
    queryKey: ["perkpoint", "segments", programId],
    queryFn: () => MembersApi.segments(programId!),
    enabled: Boolean(programId),
  });

  const query: MemberListQuery = {
    search: debouncedSearch || undefined,
    quick_tab: QUICK_TAB_PARAM[quickTab],
    tier: tierFilter === "all" ? undefined : tierFilter,
    status: statusFilter === "all" ? undefined : statusFilter,
    segment: segmentFilter === "all" ? undefined : segmentFilter,
    activity: activityFilter === "all" ? undefined : activityFilter,
    from: isoDay(dateRange?.from),
    to: isoDay(dateRange?.to ?? dateRange?.from),
    page,
    pageSize,
  };
  const membersQuery = useQuery({
    queryKey: memberKeys.list(programId ?? "", query),
    queryFn: () => MembersApi.list(programId!, query),
    enabled: Boolean(programId),
    placeholderData: keepPreviousData,
  });
  const filteredMembers = useMemo(
    () => (membersQuery.data?.items ?? []).map(rowFromApi),
    [membersQuery.data],
  );
  const totalMembers = membersQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalMembers / pageSize));
  const changeFilter = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  const statusMutation = useMutation({
    mutationFn: ({ member, block }: { member: MemberRow; block: boolean }) =>
      block
        ? MembersApi.block(programId!, member.id)
        : MembersApi.unblock(programId!, member.id),
    onSuccess: (_, { member, block }) => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      toast.success(`${member.name} has been ${block ? "blocked" : "unblocked"}`);
      setMemberToBlock(null);
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not change the member's status")),
  });

  const confirmBlockMember = () => {
    if (memberToBlock) statusMutation.mutate({ member: memberToBlock, block: true });
  };

  const allVisibleSelected =
    filteredMembers.length > 0 &&
    filteredMembers.every((m) => selectedIds.includes(m.id));

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filteredMembers.map((m) => m.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((value) => value !== id),
    );
  };

  return (
    <div className="mx-auto min-w-0 max-w-6xl space-y-8 pb-4">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-[1.75rem] font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Members
          </h1>
          <p className="mt-2 max-w-xl text-sm text-slate-500 sm:text-base">
            Find someone in the program, or add a member.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <DatePickerWithRange
            presets
            date={dateRange}
            setDate={changeFilter(setDateRange)}
            className="w-full sm:w-auto [&_button]:h-11 [&_button]:rounded-full [&_button]:bg-white [&_button]:text-slate-700"
          />
          <Button
            type="button"
            className="h-11 rounded-full bg-[#1B7A45] text-white hover:bg-[#16653A]"
            onClick={() => setAddMemberOpen(true)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add member
          </Button>
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-col gap-3 rounded-[24px] bg-white p-3 ring-1 ring-black/[0.06]">
        <div className="relative min-w-0">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name, email or member ID"
            className="h-10 pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={tierFilter} onValueChange={changeFilter(setTierFilter)}>
            <SelectTrigger className="h-10 w-[130px] bg-white">
              <SelectValue placeholder="All tiers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tiers</SelectItem>
              {(programQuery.data?.tiers ?? []).map((tier) => (
                <SelectItem key={tier.key} value={tier.key}>
                  {tier.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={changeFilter(setStatusFilter)}>
            <SelectTrigger className="h-10 w-[140px] bg-white">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="at_risk">At risk</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={segmentFilter} onValueChange={changeFilter(setSegmentFilter)}>
            <SelectTrigger className="h-10 w-[140px] bg-white">
              <SelectValue placeholder="All segments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All segments</SelectItem>
              {(segmentsQuery.data ?? []).map((segment) => (
                <SelectItem key={segment.key} value={segment.key}>
                  {segment.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={activityFilter} onValueChange={changeFilter(setActivityFilter)}>
            <SelectTrigger className="h-10 w-[140px] bg-white">
              <SelectValue placeholder="All activity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All activity</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value="current" disabled>
            <SelectTrigger className="h-10 w-[170px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">
                {programQuery.data?.name ?? "This program"}
              </SelectItem>
            </SelectContent>
          </Select>

          <Button type="button" variant="outline" className="h-10 bg-white">
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            More filters
          </Button>
        </div>
      </div>

      {/* Table card */}
      <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-slate-950">
                  Who to work
                </h2>
                <p className="mt-1 max-w-xl text-sm text-slate-500">
                  Tier, balance, last activity, and risk stay on the row. The
                  line under each name is the next action, read from those
                  fields.
                  {selectedIds.length > 0
                    ? ` ${selectedIds.length} selected.`
                    : ""}
                </p>
              </div>
              <Button variant="outline" className="h-9 w-full bg-white sm:w-auto">
                <Columns3 className="mr-2 h-4 w-4" />
                Columns
              </Button>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex flex-wrap gap-2">
                {QUICK_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setQuickTab(tab.id);
                      setPage(1);
                    }}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                      quickTab === tab.id
                        ? "border-[#1B7A45] bg-emerald-50 text-emerald-900"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(checked) =>
                        toggleSelectAll(Boolean(checked))
                      }
                      aria-label="Select all members"
                    />
                  </TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Member ID</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Points balance</TableHead>
                  <TableHead>Points earned</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last active</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      {!programId || membersQuery.isLoading
                        ? "Loading members…"
                        : membersQuery.isError
                          ? apiErrorMessage(membersQuery.error, "Could not load members")
                          : "No members match the selected filters."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(member.id)}
                          onCheckedChange={(checked) =>
                            toggleSelectOne(member.id, Boolean(checked))
                          }
                          aria-label={`Select ${member.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback
                              className={cn(
                                "text-xs font-semibold",
                                member.avatarClass,
                              )}
                            >
                              {member.initials}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <Link
                              to={`/perkpoint/member/${member.id}`}
                              className="font-semibold text-slate-950 hover:text-emerald-800"
                            >
                              {member.name}
                            </Link>
                            <p className="truncate text-xs text-slate-500">
                              {member.email}
                            </p>
                            <p
                              className={cn(
                                "mt-1 text-xs font-medium",
                                member.status === "Active"
                                  ? "text-emerald-800"
                                  : "text-amber-800",
                              )}
                            >
                              {member.nextAction}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {member.memberId}
                      </TableCell>
                      <TableCell>
                        <TierBadge tier={member.tier} />
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-sm font-medium text-green-800">
                          <Coins className="h-3.5 w-3.5" />
                          {formatPts(member.pointsBalance)}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {formatPts(member.pointsEarned)}
                      </TableCell>
                      <TableCell>
                        <StatusCell status={member.status} />
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {member.lastActive}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/perkpoint/member/${member.id}`}>
                                <Eye className="mr-2 h-4 w-4" />
                                View details
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/perkpoint/member/${member.id}?edit=1`}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit member
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setMemberToAdjust(member)}>
                              <Coins className="mr-2 h-4 w-4" />
                              Adjust points
                            </DropdownMenuItem>
                            {member.status === "Blocked" ? (
                              <DropdownMenuItem
                                onClick={() =>
                                  statusMutation.mutate({ member, block: false })
                                }
                              >
                                <LockOpen className="mr-2 h-4 w-4" />
                                Unblock member
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() => setMemberToBlock(member)}
                              >
                                <Ban className="mr-2 h-4 w-4" />
                                Block member
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {totalMembers === 0
                ? "No members"
                : `Showing ${(page - 1) * pageSize + 1}-${(page - 1) * pageSize + filteredMembers.length} of ${totalMembers.toLocaleString("en-IN")} members`}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-2 text-sm text-slate-700">
                Page {page} of {totalPages.toLocaleString("en-IN")}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AddMemberSheet
        open={addMemberOpen}
        onOpenChange={setAddMemberOpen}
        programId={programId}
        startingTier={programQuery.data?.startingTier ?? ""}
      />

      {programId && memberToAdjust ? (
        <AdjustMemberPointsDialog
          open
          onOpenChange={(open) => {
            if (!open) setMemberToAdjust(null);
          }}
          programId={programId}
          memberId={memberToAdjust.id}
          memberName={memberToAdjust.name}
          available={memberToAdjust.pointsBalance}
          onAdjusted={() => {
            queryClient.invalidateQueries({ queryKey: memberKeys.all });
          }}
        />
      ) : null}

      <AlertDialog
        open={Boolean(memberToBlock)}
        onOpenChange={(open) => {
          if (!open) setMemberToBlock(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to block{" "}
              <span className="font-medium text-slate-900">
                {memberToBlock?.name}
              </span>
              ? They will no longer be able to earn or redeem points in this
              program.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmBlockMember}
            >
              <Ban className="mr-2 h-4 w-4" />
              Block member
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
